'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import DemoCredentials from './DemoCredentials';
import { Trophy, Zap, Users, Star, Shield } from 'lucide-react';

export default function AuthScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  const brandHighlights = [
    { icon: Trophy, text: '$8.2M+ in prizes awarded', color: '#F59E0B' },
    { icon: Zap, text: '500+ competition types', color: '#A78BFA' },
    { icon: Users, text: '2.4M+ active players', color: '#06B6D4' },
    { icon: Star, text: '4.8/5 player satisfaction', color: '#10B981' },
    { icon: Shield, text: 'Secure & fully compliant', color: '#F43F5E' },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--background)' }}>
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[560px] flex-shrink-0 relative overflow-hidden p-10"
        style={{
          background: 'linear-gradient(160deg, #0D1424 0%, #1a0a2e 50%, #0D1424 100%)',
          borderRight: '1px solid var(--border)',
        }}>
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full opacity-20 blur-3xl"
            style={{ background: 'radial-gradient(circle, #7C3AED, transparent)' }} />
          <div className="absolute bottom-1/4 left-1/4 w-60 h-60 rounded-full opacity-15 blur-3xl"
            style={{ background: 'radial-gradient(circle, #06B6D4, transparent)' }} />
        </div>

        {/* Logo */}
        <Link href="/" className="flex items-center gap-3 relative z-10">
          <AppLogo size={40} />
          <span className="text-xl font-bold text-foreground">Brain Basket</span>
        </Link>

        {/* Center content */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
            style={{ background: 'rgba(124, 58, 237, 0.2)', border: '1px solid rgba(124, 58, 237, 0.3)' }}>
            <span className="status-dot-live" />
            <span className="text-xs font-semibold" style={{ color: '#A78BFA' }}>1,847 players online now</span>
          </div>

          <h2 className="font-extrabold mb-4 text-foreground" style={{ fontSize: '36px', lineHeight: '1.15', letterSpacing: '-0.03em' }}>
            Your Skills.<br />
            <span className="text-gradient-primary">Your Earnings.</span>
          </h2>
          <p className="text-base mb-8 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Join 2.4 million professionals, students, and creators who compete, learn, and earn on Brain Basket every day.
          </p>

          <div className="space-y-3">
            {brandHighlights.map((h) => (
              <div key={`bh-${h.text}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${h.color}20`, border: `1px solid ${h.color}30` }}>
                  <h.icon size={14} style={{ color: h.color }} />
                </div>
                <span className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>{h.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom quote */}
        <div className="relative z-10 p-4 rounded-xl" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
          <p className="text-sm italic mb-2" style={{ color: 'var(--muted-foreground)' }}>
            &ldquo;Won ₹12,000 in my first month just from coding challenges. Brain Basket is the real deal.&rdquo;
          </p>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: '#7C3AED30', color: '#A78BFA' }}>R</div>
            <span className="text-xs font-semibold" style={{ color: '#A78BFA' }}>Rahul D. — Developer, Pune</span>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 overflow-y-auto">
        {/* Mobile logo */}
        <Link href="/" className="flex items-center gap-3 mb-8 lg:hidden">
          <AppLogo size={36} />
          <span className="text-lg font-bold text-foreground">Brain Basket</span>
        </Link>

        <div className="w-full max-w-md">
          {/* Tabs */}
          <div className="flex rounded-xl p-1 mb-8" style={{ background: 'var(--muted)' }}>
            {(['login', 'register'] as const).map((tab) => (
              <button
                key={`auth-tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: activeTab === tab ? 'var(--primary)' : 'transparent',
                  color: activeTab === tab ? '#fff' : 'var(--muted-foreground)',
                  boxShadow: activeTab === tab ? '0 2px 8px rgba(124, 58, 237, 0.4)' : 'none',
                }}
              >
                {tab === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {activeTab === 'login' ? (
            <LoginForm onSwitchToRegister={() => setActiveTab('register')} />
          ) : (
            <RegisterForm onSwitchToLogin={() => setActiveTab('login')} />
          )}

          <DemoCredentials onSelect={(email, password, role) => {
            setActiveTab('login');
          }} />
        </div>
      </div>
    </div>
  );
}